import { useEffect, useState } from 'react';
import {
  FiArrowLeft as ArrowLeft,
  FiCheckCircle as CheckCircle2,
  FiChevronDown as ChevronDown,
  FiHelpCircle as HelpCircle,
  FiInfo as Info,
} from 'react-icons/fi';
import { TbSparkles as Sparkles } from 'react-icons/tb';
import { walletPresetAmounts } from '../pricing';
import { minimumWalletTopUp, ui, walletConfig } from '../constants';
import {
  formatAmountInput,
  formatToman,
  getWalletAmountError,
  isValidWalletAmount,
  normalizeWalletAmount,
  parseTomanInput,
} from '../utils';

interface WalletTopUpCardProps {
  enabled: boolean;
  onToggle: (checked: boolean) => void;
  amount: number;
  onAmountChange: (amount: number) => void;
}

type WalletUsageItem = (typeof walletConfig.usage.items)[number];

function WalletUsageLabel({ item }: { item: WalletUsageItem }) {
  if (item.variant === 'purchase') {
    return <>{item.feature}</>;
  }

  if (item.comingSoon) {
    return <>{item.tool} (بزودی)</>;
  }

  return (
    <>
      {item.tool}
      <ArrowLeft size={12} className="wallet-usage-arrow" aria-hidden="true" />
      {item.feature}
    </>
  );
}

function WalletTopUpCard({
  enabled,
  onToggle,
  amount,
  onAmountChange,
}: WalletTopUpCardProps) {
  const [customAmount, setCustomAmount] = useState(formatAmountInput(amount));
  const [hasInteracted, setHasInteracted] = useState(false);
  const [usageExpanded, setUsageExpanded] = useState(true);

  useEffect(() => {
    setCustomAmount(formatAmountInput(amount));
  }, [amount]);

  const parsedAmount = parseTomanInput(customAmount);
  const validationError =
    parsedAmount > 0 ? getWalletAmountError(parsedAmount) : null;
  const showValidationError =
    enabled && hasInteracted && Boolean(validationError);
  const suggestedAmount =
    showValidationError && validationError
      ? normalizeWalletAmount(parsedAmount)
      : null;

  function updateCustomAmount(value: string) {
    const parsed = parseTomanInput(value);
    setCustomAmount(formatAmountInput(parsed));
    onToggle(true);

    if (isValidWalletAmount(parsed)) {
      onAmountChange(parsed);
    }
  }

  function normalizeCustomAmount() {
    const parsed = parseTomanInput(customAmount);
    if (parsed <= 0) return;

    setHasInteracted(true);

    if (isValidWalletAmount(parsed)) {
      onAmountChange(parsed);
      setCustomAmount(formatAmountInput(parsed));
      return;
    }

    // Keep typed value so the user sees the error + suggested fix.
  }

  function applySuggestedAmount() {
    if (suggestedAmount == null) return;
    setCustomAmount(formatAmountInput(suggestedAmount));
    onAmountChange(suggestedAmount);
    setHasInteracted(true);
  }

  function toggleWalletTopUp(checked: boolean) {
    if (checked) onAmountChange(amount || minimumWalletTopUp);
    onToggle(checked);
    if (!checked) setHasInteracted(false);
  }

  return (
    <section className={`wallet-topup-card rounded-4 ${enabled ? 'enabled' : 'bg-white dark__bg-card-dark'}`}>
      <div className="tracker-top">
        <div className="tracker-title">
          <span className="tracker-icon wallet-topup-icon rounded-3">
            <Sparkles size={26} />
          </span>
          <div>
            <span className="eyebrow wallet-topup-eyebrow fs--2">{walletConfig.eyebrow}</span>
            <h2 className="text-1000">{walletConfig.title}</h2>
            <p className="text-secondary fs--2">{walletConfig.cardDescription}</p>
          </div>
        </div>

        <label className="switch-label wallet-topup-switch fs--2">
          <span className="text-secondary">{enabled ? ui.common.active : ui.common.addToPlan}</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => toggleWalletTopUp(event.target.checked)}
          />
          <span className="switch rounded-pill" aria-hidden="true">
            <span className="rounded-circle" />
          </span>
        </label>
      </div>

      <div className="wallet-topup-body">
        <div className="wallet-presets-column">
          <span className="control-label text-900 fs--2">{walletConfig.presetLabel}</span>
          <div className="wallet-presets">
            {walletPresetAmounts.map((preset) => (
              <button
                type="button"
                key={preset}
                className={enabled && amount === preset ? 'active rounded-3 text-900' : 'rounded-3 text-secondary'}
                disabled={!enabled}
                onClick={(event) => {
                  event.stopPropagation();
                  setHasInteracted(false);
                  onAmountChange(preset);
                }}
              >
                {formatToman(preset)}
              </button>
            ))}
          </div>
        </div>

        <div className="wallet-custom-amount">
          <label className="text-900 fs--2" htmlFor="wallet-topup-amount">{walletConfig.customAmountLabel}</label>
          <div
            className={`number-field rounded-3${showValidationError ? ' is-invalid' : ''}`}
          >
            <input
              id="wallet-topup-amount"
              type="text"
              inputMode="numeric"
              value={customAmount}
              disabled={!enabled}
              aria-invalid={showValidationError}
              aria-describedby="wallet-topup-amount-help"
              onBlur={normalizeCustomAmount}
              onChange={(event) => updateCustomAmount(event.target.value)}
            />
            <span className="text-secondary fs--2">{ui.common.toman}</span>
          </div>

          {showValidationError ? (
            <div
              id="wallet-topup-amount-help"
              className="wallet-amount-error"
              role="alert"
            >
              <span>{validationError}</span>
              {suggestedAmount != null && suggestedAmount !== parsedAmount ? (
                <button
                  type="button"
                  className="wallet-amount-suggest"
                  onClick={applySuggestedAmount}
                >
                  استفاده از مبلغ پیشنهادی: {formatToman(suggestedAmount)}
                </button>
              ) : null}
            </div>
          ) : (
            <small id="wallet-topup-amount-help" className="wallet-amount-hint">
              {walletConfig.hint}
            </small>
          )}
        </div>

        <article className="mentoring-plan wallet-usage-plan rounded-3">
          <button
            type="button"
            className="wallet-usage-toggle"
            onClick={() => setUsageExpanded((current) => !current)}
            aria-expanded={usageExpanded}
          >
            <span className="wallet-usage-title-group">
              <span className="wallet-usage-help-icon rounded-circle" aria-hidden="true">
                <HelpCircle size={13} />
              </span>
              <strong className="text-1000">{walletConfig.usage.title}</strong>
            </span>
            <ChevronDown size={16} className={usageExpanded ? 'rotated' : ''} aria-hidden="true" />
          </button>

          {usageExpanded ? (
            <ul className="mentoring-features text-secondary fs--2">
              {walletConfig.usage.items.map((item, index) => {
                const itemKey = [item.tool, item.feature, item.variant, index].filter(Boolean).join('-');

                return (
                  <li key={itemKey}>
                    <CheckCircle2 size={15} />
                    <span className="wallet-usage-label">
                      <WalletUsageLabel item={item} />
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </article>

        <div className="tracker-refund-note fs--2 rounded-3">
          <Info size={16} />
          <span>{walletConfig.refundNote}</span>
        </div>
      </div>
    </section>
  );
}

export default WalletTopUpCard;
